import { Injectable } from '@angular/core';
import { LoginCognitoCallback, CognitoUtil } from '../utils/cognito.service';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { StateManagementService } from './state-management.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ERROR_CODES } from '../constants/error-codes';
import { OperationFailedError } from '../errors/errors';
import { BRANDS } from '../models/screen-size.enum';
import { CONSTANTS } from '../constants/constants';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from '../utils/common.service';
import { Router } from '@angular/router';
import LoginRoutingPath from 'src/app/login/login-routing-path';

@Injectable({
  providedIn: 'root',
})
export class UserLoginService {
  private url = environment.graphqlUrl;
  private loginCallback?: LoginCognitoCallback; //store the callback from authenticate()

  constructor(
    public cognitoUtil: CognitoUtil,
    private stateManagementService: StateManagementService,
    private http: HttpClient,
    private translate: TranslateService,
    private commonService: CommonService,
    private router: Router
  ) {}

  public setLoginCallback = (cb: LoginCognitoCallback) => {
    this.loginCallback = cb;
  };
  public clearLoginCallback = () => {
    this.loginCallback = undefined;
  };

  public onLoginSuccess = (session: CognitoUserSession) => {
    this.updateNotification().subscribe({
      next: () => {
        if (window.__dev__)
          console.log('Notification preferences updated successfully.');
      },
      error: (err) => {
        if (window.__dev__)
          console.error('Failed to update notification preferences:', err);
      },
    });
    if (this.loginCallback?.cognitoCallback) {
      this.loginCallback.cognitoCallback('', session);
    } else if (window.__dev__) {
      console.warn('Login callback not set; skipped cognitoCallback');
    }
  };

  public onLoginError = (err: any) => {
    if (this.loginCallback?.cognitoCallback) {
      this.loginCallback.cognitoCallback(err?.message ?? 'Login failed', null);
    } else if (window.__dev__) {
      console.warn('Login callback not set; error could not be forwarded', err);
    }
  };

  authenticate(
    username: string,
    password: string,
    callback: LoginCognitoCallback
  ) {
    this.setLoginCallback(callback);
    const query = `mutation ($username: String!, $password: String!) { authenticateUser(username: $username, password: $password) {
      usEast1 { success idtoken accesstoken refreshtoken expiresat } usWest2 { success idtoken accesstoken refreshtoken expiresat }
      challenge { challenge challengename session username phonenumbersuffix medium isphoneverified } } }`;
    const headers = {
      Authorization: 'NO_AUTHENTICATION',
      AuthorizationSecondary: 'NO_AUTHENTICATION',
    };

    const variables = {
      username,
      password,
    };

    this.http
      .post<any>(
        `${this.url}`,
        {
          query,
          variables,
        },
        { headers }
      )
      .subscribe({
        next: (response) => {
          console.log('GraphQL response:', response);

          if (response.errors && response.errors.length > 0) {
            const error = response?.errors[0];
            const errorMessage = error.message;
            console.error('GraphQL error:', error);
            this.onLoginError(errorMessage);
            return;
          }

          const data = response.data.authenticateUser;
          if (data.challenge) {
            callback.handleMFAStep(data.challenge, (err, res) => {
              if (err) {
                this.onLoginError(err);
              }
            });
          } else if (data.usEast1 || data.usWest2) {
            this.commonService.storeRegionTokens(data, username, false);
            this.onLoginSuccess(data);
          }
        },
        error: (err) => {
          console.error('GraphQL error:', err);
          this.onLoginError(err);
        },
      });
  }

  logout(): Observable<any> {
    const query = `query { userLogout { success } } `;
    return this.http.post<any>(environment.graphqlUrl, { query }).pipe(
      map((response) => {
        if (response?.data?.userLogout?.success) {
          this.logoutLocally();
        }
        return response;
      }),
      catchError((err) => {
        console.error('Logout error:', err);
        // Clear cache even if logout API fails
        this.logoutLocally();
        return of(err);
      })
    );
  }
  logoutLocally(): void {
    this.clearAllCache();
    this.router.navigate([LoginRoutingPath.loginForm]);
  }

  /**
   * Clear all cached data including localStorage, sessionStorage, and Redux state
   * @returns void
   */
  private clearAllCache(): void {
    // Remove authentication tokens
    this.commonService.removeTokens();

    // Clear Redux state
    this.stateManagementService.logout();

    // Clear all localStorage except rememberMe if needed
    const rememberMeData = localStorage.getItem('state');
    let preserveRememberMe = null;

    if (rememberMeData) {
      try {
        const state = JSON.parse(rememberMeData);
        if (state?.rememberMe?.state) {
          preserveRememberMe = state.rememberMe;
        }
      } catch (e) {
        console.error('Error parsing state for rememberMe:', e);
      }
    }

    // Clear all localStorage
    localStorage.clear();

    // Restore rememberMe if it was enabled
    if (preserveRememberMe) {
      const initialState = {
        rememberMe: preserveRememberMe,
      };
      localStorage.setItem('state', JSON.stringify(initialState));
    }

    // Clear all sessionStorage
    sessionStorage.clear();
  }

  getUserAccessibilityMatrix(): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: 'query {  getUserAccessibilityMatrix {  allowedActions  }  }',
      })
      .pipe(
        map((res) => {
          try {
            const resData = JSON.parse(
              res?.data?.getUserAccessibilityMatrix?.allowedActions
            );
            return resData;
          } catch (exception) {
            throw new Error(ERROR_CODES.BAD_ARGUMENT);
          }
        })
      );
  }

  /**
   * validate email already exist or not then share send opt via a email | phone
   * @param email string user define email address
   * @param type type should be phone | email
   * @returns return server response result
   */
  forgotPassword(email: string, type = 'phone'): Observable<any> {
    const headers = {
      Authorization: 'NO_AUTHENTICATION',
      AuthorizationSecondary: 'NO_AUTHENTICATION',
    };

    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `mutation forgotPassword($email: String!, $verificationtype: String!) {  forgotPassword(email: $email, verificationtype: $verificationtype) { ${type} }}`,
          operationName: 'forgotPassword',
          variables: {
            email: `${email}`,
            verificationtype: `${type}`,
          },
        },
        { headers }
      )
      .pipe(
        map((res) => {
          if (res?.data?.forgotPassword) {
            return res?.data;
          } else if (res.hasOwnProperty('errors') && res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_SEND_CODE_TRY_AGAIN')
              );
            case ERROR_CODES.BAD_ARGUMENT:
              throw new OperationFailedError(
                this.translate.instant('YOU_MUST_ENTER_A_VALID_EMAIL')
              );

            default:
              throw new Error(
                this.translate.instant('FAILED_TO_SEND_CODE_TRY_AGAIN')
              );
          }
        })
      );
  }

  /**
   *
   * @param code code from email | phone
   * @param email email address
   * @param medium  phone | email
   * @param codetype default forgotpassword server side
   * @returns  return server response result
   */
  passwordRecoveryOTP(
    code: string,
    email: string,
    medium = 'phone',
    codetype = 'forgotpassword'
  ): Observable<any> {
    const headers = {
      Authorization: 'NO_AUTHENTICATION',
      AuthorizationSecondary: 'NO_AUTHENTICATION',
    };

    return this.http
      .post<any>(
        `${this.url}`,
        {
          query:
            'mutation forgotPasswordVerifyCode($code: String!, $codetype: String!, $email: String!, $medium: String!) { forgotPasswordVerifyCode(code: $code, codetype: $codetype, email: $email, medium: $medium) { success } }',
          variables: {
            code: `${code}`,
            codetype: `${codetype}`,
            email: `${email}`,
            medium: `${medium}`,
          },
          operationName: 'forgotPasswordVerifyCode',
        },
        { headers }
      )
      .pipe(
        map((res) => {
          const resData = res?.data;
          if (resData?.forgotPasswordVerifyCode?.success) {
            return resData;
          } else if (res.hasOwnProperty('errors') && res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.VALUE_MISS_MATCH:
              throw new OperationFailedError(
                this.translate.instant('VERIFICATION_CODE_IS_INCORRECT')
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('OTP_VERIFICATION_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('OTP_VERIFICATION_FAILED')
              );
          }
        })
      );
  }

  /**
   *
   * @param email email address
   * @param password password
   * @returns return server response result
   */
  passwordUpdate(email: string, password: string): Observable<any> {
    const headers = {
      Authorization: 'NO_AUTHENTICATION',
      AuthorizationSecondary: 'NO_AUTHENTICATION',
    };

    return this.http
      .post<any>(
        `${this.url}`,
        {
          query:
            'mutation forgotPasswordConfirm($email: String!, $password: String!) { forgotPasswordConfirm(email: $email, password: $password) { success } }',
          variables: {
            email: `${email}`,
            password: `${password}`,
          },
          operationName: 'forgotPasswordConfirm',
        },
        { headers }
      )
      .pipe(
        map((res) => {
          const resData = res?.data;
          if (resData?.forgotPasswordConfirm?.success) {
            return resData;
          } else if (res.hasOwnProperty('errors') && res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_UPDATED_PASSWORD_TRY_AGAIN')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_UPDATED_PASSWORD_TRY_AGAIN')
              );
          }
        })
      );
  }

  updateNotification(): Observable<any> {
    const brand = this.stateManagementService?.getUserBrand();
    const language = this.stateManagementService?.getUserLanguage();
    const brandName =
      brand === BRANDS.KWIKSETUNITE
        ? CONSTANTS.KWIKSETUNITE
        : CONSTANTS.WEISERUNITE;
    const payload = {
      query: `
        mutation updateNotification($devicetoken: String!, $action: String!, $language: String!, $brandName: String!) {
          updateNotification(devicetoken: $devicetoken, action: $action, language: $language, brandName: $brandName) {
            success
          }
        }
      `,
      variables: {
        devicetoken: 'Web',
        action: 'register',
        language: language as string,
        brandName: brandName as string,
      },
      operationName: 'updateNotification',
    };

    return this.http.post<any>(this.url, payload).pipe(
      map((res) => {
        if (res?.data?.updateNotification?.success) {
          return res?.data;
        } else if (res.hasOwnProperty('errors') && res?.errors?.length > 0) {
          throw new Error(res?.errors[0]?.errorType);
        }
      }),
      catchError((err: any) => {
        throw err;
      })
    );
  }

  verifyMFA(code: any, sessionInfo: any): Observable<any> {
    const { session, username, password } = sessionInfo;
    const medium = this.resolveMfaMedium(sessionInfo);
    console.log(
      'Verifying MFA with code:',
      code,
      'session:',
      session,
      'username:',
      username,
      'password:',
      password,
      'medium:',
      medium
    );

    const query = ` mutation ($code: String!,  $session: String!, $username: String!, $password: String!, $medium: String!) {
        verifyMFA(code: $code,  session: $session, username: $username,  password: $password, medium: $medium) {
            usEast1 {
                success
                idtoken
                accesstoken
                refreshtoken
                expiresat
            }
            usWest2 {
                success
                idtoken
                accesstoken
                refreshtoken
                expiresat
            }

            challenge {
                challenge
                challengename
                session
                username
                medium
                error {
                code
                message
            }
            }
        }
    }`;

    const headers = {
      Authorization: 'NO_AUTHENTICATION',
      AuthorizationSecondary: 'NO_AUTHENTICATION',
    };

    const variables = {
      code,
      session: sessionInfo.session,
      username: username,
      password: sessionInfo.password,
      medium,
    };

    return this.http
      .post<any>(environment.graphqlUrl, { query, variables }, { headers })
      .pipe(
        map((response) => {
          if (response?.errors?.length > 0) {
            const graphQLError = response.errors[0];
            const mfaError = new Error(
              graphQLError?.message || 'MFA verification failed'
            ) as Error & {
              session?: string;
              code?: string;
            };
            mfaError.session = sessionInfo?.session;
            mfaError.code =
              graphQLError?.extensions?.errorType ||
              graphQLError?.extensions?.code ||
              '';
            throw mfaError;
          }

          const result = response?.data?.verifyMFA;
          if (result?.usEast1?.success || result?.usWest2?.success) {
            this.commonService.storeRegionTokens(result, username, false);
            return { success: true, data: result };
          } else if (result?.challenge) {
            const latestSession =
              result?.challenge?.session ?? sessionInfo?.session;
            if (result.challenge.error) {
              const mfaError = new Error(result.challenge.error.message) as Error & {
                session?: string;
                code?: string;
              };
              mfaError.session = latestSession;
              mfaError.code = result.challenge.error.code;
              throw mfaError;
            }
            return { success: false, challenge: result.challenge, session: latestSession };
            //this.authenticate()
          } else {
            throw new Error(result?.error?.message);
          }
        })
      );
  }

  private resolveMfaMedium(sessionInfo: any): string {
    if (sessionInfo?.mfaData && typeof sessionInfo.mfaData.isMediumPhone === 'boolean') {
      return sessionInfo.mfaData.isMediumPhone ? CONSTANTS.PHONE : CONSTANTS.EMAIL;
    }
    return sessionInfo?.medium || CONSTANTS.PHONE;
  }

  onResendMFACode(
    session: any,
    username: any,
    medium: any,
    callback: any
  ): Observable<any> {
    console.log(
      'onResendCode:',
      'session:',
      session,
      'username:',
      username,
      'medium:',
      medium
    );

    const query = ` mutation ($username: String!,  $session: String!, $medium: String!) {
      resendMFA(username: $username,  session: $session, medium: $medium) { challenge challengename session username medium
        error{
          code  message
        }
      }
    }`;

    const headers = {
      Authorization: 'NO_AUTHENTICATION',
      AuthorizationSecondary: 'NO_AUTHENTICATION',
    };

    const variables = {
      session: session,
      username: username,
      medium: medium,
    };

    return this.http
      .post<any>(`${this.url}`, { query, variables }, { headers })
      .pipe(
        map((response) => {
          // Check for GraphQL errors first
          if (response?.errors && response.errors.length > 0) {
            if (this.loginCallback?.cognitoCallback) {
              this.loginCallback.cognitoCallback(
                response.errors[0].message ?? 'Login failed',
                null
              );
            } else if (window.__dev__) {
              console.warn(
                'Login callback not set; error could not be forwarded',
                response.errors
              );
            }
          }

          const result = response?.data;
          console.log('result', result);

          // Check if resendMFA returned null (indicating an error)
          if (!result?.resendMFA) {
            throw new Error('Resend MFA failed');
          }

          callback.handleMFAStep(result.resendMFA);
          return { success: true, data: result };
        })
      );
  }
}
